import type { PriceLetterLine } from '../api/letterTypes'

/** Percent change old → new, or null when it can't be computed. */
export function pctChange(oldPrice: number | null, newPrice: number | null): number | null {
  if (oldPrice === null || newPrice === null || oldPrice === 0) return null
  return ((newPrice - oldPrice) / oldPrice) * 100
}

export function formatPct(p: number | null): string {
  if (p === null) return '—'
  const rounded = Math.round(p * 10) / 10
  return `${rounded > 0 ? '+' : ''}${rounded.toFixed(1)}%`
}

export function formatMoney(symbol: string, v: number | null): string {
  if (v === null) return '—'
  return `${symbol}${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export interface SplitLines {
  priced: PriceLetterLine[]
  discontinued: PriceLetterLine[]
  excluded: PriceLetterLine[]
}

/** What the letter will print: priced and discontinued rows that are included. */
export function splitLines(lines: readonly PriceLetterLine[]): SplitLines {
  const out: SplitLines = { priced: [], discontinued: [], excluded: [] }
  for (const ln of lines) {
    if (!ln.included) out.excluded.push(ln)
    else if (ln.kind === 'discontinued') out.discontinued.push(ln)
    else out.priced.push(ln)
  }
  return out
}

export type LineColumn =
  | 'include'
  | 'product'
  | 'part'
  | 'old'
  | 'new'
  | 'change'
  | 'tariff_pct'
  | 'tariff_amount'
  | 'total'
  | 'odoo'
  | 'kind'

/** Review-grid columns; the three tariff columns exist only when toggled on. */
export function visibleColumns(showTariff: boolean): LineColumn[] {
  const base: LineColumn[] = ['include', 'product', 'part', 'old', 'new', 'change']
  if (showTariff) base.push('tariff_pct', 'tariff_amount', 'total')
  return [...base, 'odoo', 'kind']
}

/** True when Odoo's current price differs from the sheet's "old price". */
export function oldPriceMismatch(ln: PriceLetterLine): boolean {
  if (ln.odoo_current_price === null || ln.old_price === null) return false
  return Math.abs(ln.odoo_current_price - ln.old_price) > 0.005
}
